import { useEffect, useRef, useState } from 'react';
import { NJFormItem, NJIconButton, NJPopover, NJSelect, NJText } from '@engie-group/fluid-design-system-react';
import { contractLabel, contractMonths } from './domain';
import { PC, UMR, caption } from './tokens';
import type { ContractPeriod } from './types';

interface Props {
  value: ContractPeriod;
  onChange: (c: ContractPeriod) => void;
}

const MONTHS = [
  ['01', 'janvier', 'janv.'], ['02', 'février', 'févr.'], ['03', 'mars', 'mars'], ['04', 'avril', 'avr.'],
  ['05', 'mai', 'mai'], ['06', 'juin', 'juin'], ['07', 'juillet', 'juil.'], ['08', 'août', 'août'],
  ['09', 'septembre', 'sept.'], ['10', 'octobre', 'oct.'], ['11', 'novembre', 'nov.'], ['12', 'décembre', 'déc.'],
] as const;

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 101 }, (_, index) => String(currentYear - 50 + index));

function formatMonth(value: string) {
  const [year, month] = value.split('-');
  const label = MONTHS.find(([number]) => number === month)?.[2];
  return label && year ? `${label} ${year}` : '';
}

function MonthField({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  const [year = '', month = ''] = value.split('-');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => formatMonth(value));
  const [invalidDraft, setInvalidDraft] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(formatMonth(value));
    setInvalidDraft(false);
  }, [value]);

  const normalizeMonth = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\./g, '');
  const parseMonth = (text: string) => {
    const match = text.trim().toLocaleLowerCase('fr-FR').match(/^([a-zà-ÿ.]+)\s+(\d{4})$/);
    if (!match) return null;
    const monthName = normalizeMonth(match[1]);
    const monthValue = MONTHS.find(([, name, shortName]) =>
      normalizeMonth(name) === monthName || normalizeMonth(shortName) === monthName,
    )?.[0];
    const nextYear = Number(match[2]);
    if (!monthValue || nextYear < Number(YEARS[0]) || nextYear > Number(YEARS[YEARS.length - 1])) return null;
    return `${nextYear}-${monthValue}`;
  };

  const updateDraft = (text: string) => {
    setDraft(text);
    const parsed = parseMonth(text);
    setInvalidDraft(!parsed);
    if (parsed) onChange(parsed);
  };

  const openPicker = () => {
    const parsed = parseMonth(draft);
    if (parsed) onChange(parsed);
    setOpen((isOpen) => !isOpen);
  };

  const selectMonth = (nextMonth: string) => {
    onChange(`${year}-${nextMonth}`);
    setDraft(formatMonth(`${year}-${nextMonth}`));
    setInvalidDraft(false);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="pcv3-month-picker">
      <NJPopover.Root
        opened={open}
        onOpen={setOpen}
        useFloatingOptions={{ placement: 'top-start', strategy: 'fixed' }}
      >
        <NJPopover.Interactions dismiss role={{ role: 'dialog' }}>
          <NJPopover.Anchor>
            <NJFormItem
              id={`contract-${id}`}
              label={label}
              labelKind="static"
              size="sm"
              className="pcv3-contract-field"
              type="text"
              value={draft}
              placeholder="Ex. févr. 2027"
              inputRef={inputRef}
              iconName="calendar_month"
              iconTitle="Ouvrir le calendrier"
              iconClick={openPicker}
              onChange={(event) => updateDraft((event.target as HTMLInputElement).value)}
              onBlur={() => {
                if (!parseMonth(draft)) {
                  setDraft(formatMonth(value));
                  setInvalidDraft(false);
                }
              }}
              hasError={invalidDraft}
              subscriptMessage={invalidDraft ? 'Saisissez un mois et une année valides, par exemple févr. 2027.' : undefined}
              ariaLabel={label}
              aria-haspopup="dialog"
              aria-expanded={open}
              isRequired
            />
          </NJPopover.Anchor>
          <NJPopover.Content inline={false} focusManagerProps={{ modal: false }}>
            <div className="pcv3-month-picker-popover">
              <div className="pcv3-month-picker-header">
                <NJIconButton
                  icon="chevron_left"
                  label="Année précédente"
                  variant="tertiary"
                  scale="sm"
                  isDisabled={Number(year) <= Number(YEARS[0])}
                  onClick={() => onChange(`${Number(year) - 1}-${month}`)}
                />
                <div
                  className="pcv3-year-select-control"
                  onClick={(event) => {
                    if (!(event.target instanceof Element)) return;
                    const trigger = event.target.closest<HTMLElement>('[role="combobox"]');
                    if (!trigger) return;
                    window.setTimeout(() => {
                      const listId = trigger.getAttribute('aria-controls');
                      const list = listId ? document.getElementById(listId) : null;
                      const selectedYear = [...(list?.querySelectorAll<HTMLElement>('[role="option"]') ?? [])]
                        .find((option) => option.textContent?.trim() === year);
                      selectedYear?.scrollIntoView({ block: 'center' });
                    }, 0);
                  }}
                >
                  <NJSelect.Root
                    id={`contract-${id}-year`}
                    className="pcv3-year-select"
                    label="Année"
                    labelKind="static"
                    size="sm"
                    listNavigationLabel="Choisissez une année"
                    buttonDefaultValueLabel="Choisir une année"
                    value={year}
                    onChange={(next) => next && onChange(`${next}-${month}`)}
                  >
                    {YEARS.map((yearValue) => (
                      <NJSelect.Item key={yearValue} value={yearValue} label={yearValue}>
                        <NJText as="span" scale="xs">{yearValue}</NJText>
                      </NJSelect.Item>
                    ))}
                  </NJSelect.Root>
                </div>
                <NJIconButton
                  icon="chevron_right"
                  label="Année suivante"
                  variant="tertiary"
                  scale="sm"
                  isDisabled={Number(year) >= Number(YEARS[YEARS.length - 1])}
                  onClick={() => onChange(`${Number(year) + 1}-${month}`)}
                />
              </div>
              <div className="pcv3-month-picker-grid" role="group" aria-label="Mois">
                {MONTHS.map(([monthValue, monthLabel]) => (
                  <button
                    key={monthValue}
                    type="button"
                    className="pcv3-month-picker-option"
                    aria-pressed={monthValue === month}
                    onClick={() => selectMonth(monthValue)}
                  >
                    <NJText as="span" scale="xs">{monthLabel}</NJText>
                  </button>
                ))}
              </div>
            </div>
          </NJPopover.Content>
        </NJPopover.Interactions>
      </NJPopover.Root>
    </div>
  );
}

/** Période du contrat : mois de début et mois de fin, bornes incluses. */
export function ContractFields({ value, onChange }: Props) {
  const months = contractMonths(value);
  const invalid = months === 0;

  return (
    <div className="pcv3-contract-period">
      <MonthField id="start" label="Début du contrat" value={value.start} onChange={(start) => onChange({ ...value, start })} />
      <MonthField id="end" label="Fin du contrat" value={value.end} onChange={(end) => onChange({ ...value, end })} />
      <div className="pcv3-contract-summary" style={{ ...caption, color: invalid ? PC.dangerText : UMR.textSecondary }}>
        {invalid ? 'La fin du contrat doit être postérieure ou égale au début.' : contractLabel(value)}
      </div>
    </div>
  );
}
